import { useEffect, useState } from 'react';

import '../styles/app-states.css';
import { DartMark } from './DartMark.tsx';
import { Nib } from './Nib.tsx';

interface MovedProps {
	/** The site's host, as the player reads it (`sparrowword.com`). */
	host: string;
	/** Where the button goes: the site, in the browser, with the carry code when there is one. */
	href: string;
	/** The same address as a plain https link, for Copy link. */
	plain: string;
	/** What happened to the player's data: carried (a code), none to carry, or it failed. */
	carry: 'carried' | 'none' | 'failed';
	onRetry: () => void;
}

/**
 * The old home-screen app's last screen (pwa/move.ts): Sparrow has moved, open it there
 * and add it to the home screen again. The page shell of the error state (the masthead,
 * Nib, one line and an ink pill), on the page's left edge.
 */
export function MovedScreen({ host, href, plain, carry, onRetry }: MovedProps) {
	const [copied, setCopied] = useState(false);
	useEffect(() => {
		document.title = `Sparrow has moved to ${host}`;
	}, [host]);
	const sub =
		carry === 'failed'
			? 'Couldn’t bring your streak across just now. It’s still saved here, so try again when you’re online.'
			: carry === 'carried'
				? 'Your streak and stats come with you. Once it’s open, add it to your home screen again and remove this one.'
				: 'Once it’s open, add it to your home screen again and remove this one.';
	return (
		<div className="app app-state moved">
			<header className="page-mast state-mast">
				<p className="brand">
					<DartMark className="brand-mark" />
					<span className="wm">sparrow</span>
				</p>
			</header>
			<main className="state state-error">
				<div className="state-body">
					<Nib pose="cheer" className="state-nib" />
					<h1 className="state-line">Sparrow has moved to {host}</h1>
					<p className="state-sub">{sub}</p>
					<div className="state-actions">
						{carry === 'failed' ? (
							<button type="button" className="btn lg primary fit" onClick={onRetry}>
								Try again
							</button>
						) : (
							<a className="btn lg primary fit" href={href} target="_blank" rel="noopener">
								Open {host}
							</a>
						)}
						<button
							type="button"
							className="state-link"
							onClick={() => {
								void navigator.clipboard?.writeText(plain).then(
									() => setCopied(true),
									() => undefined,
								);
							}}
						>
							{copied ? 'Copied' : 'Copy link'}
						</button>
					</div>
				</div>
			</main>
		</div>
	);
}
